/**
 * useParentDashboard hook
 * React Query hook for fetching the parent's two-bucket Otter Coin balance and
 * full dashboard data. Provides coin balance, derived isNewFamily flag, children
 * summaries, in-progress session, engagement summary, loading, error, and refetch capabilities.
 *
 * Implements US-006 (T3): useParentDashboard hook + isNewFamily derivation
 * Modified  US-008 (T4): hook return now also exposes children and inProgressSession
 *   so ParentDashboardPage can compose the Overview tab without a second query.
 *   Single React Query cache key ['parent','dashboard'] — one fetch, all tabs.
 * Modified  US-014 (T1): hook return now also exposes engagementSummary (typed EngagementSummary)
 *   for the RecentActivitySummary component. Additive field — backward compatible.
 *   TO-LL-036: engagementSummary passes data?.engagementSummary through unchanged; undefined
 *   when absent. Errors are never swallowed — the React Query error object surfaces as before.
 * Modified  TS-013 (T4): coinBalance now carries topupNearestExpiryDate/topupNearestExpiryAmount
 *   (additive — OtterCoinBalance was extended in types/coins.ts T1). This hook is a PURE
 *   PASSTHROUGH: no restructuring, no derived formatting (that belongs to TS-014's indicator UI).
 *   topupCoins itself is unchanged in shape — its value is now backend-derived as the
 *   non-expired-batch sum (TS-006), but this hook does not need to know that.
 *   BR-TS013.1 / CCM-02: monthlyCoins and topupCoins remain distinct fields on coinBalance —
 *   this hook never merges them into a single total.
 *
 * Modelled directly on the existing useDashboard hook (frontend/src/hooks/useDashboard.ts).
 *
 * isNewFamily derivation rule (data-driven, no new backend field required):
 *   A brand-new family has no active monthly subscription bucket (monthlyCoins === 0)
 *   AND a positive top-up bucket holding the welcome coin (topupCoins > 0)
 *   AND no in-progress session (inProgressSession == null).
 *   This means the welcome banner is shown only to families that have the welcome
 *   coin and have not yet started any session.
 */

import { useQuery } from '@tanstack/react-query';
import { getParentDashboard } from '@/services/parent';
import type { OtterCoinBalance, ChildSummary, InProgressSession, EngagementSummary, LiveSession, ParentDashboardResponse } from '@/types/coins';

/**
 * Return type for useParentDashboard hook
 * US-008 (T4): Added children and inProgressSession fields (additive, backward-compatible).
 * US-014 (T1): Added engagementSummary field (additive, backward-compatible).
 *   TO-LL-036: engagementSummary is undefined when the backend omits the key; no silent fallback.
 */
export interface UseParentDashboardReturn {
  /**
   * The parent's two-bucket coin balance (undefined during loading).
   * TS-013 (T4): also carries topupNearestExpiryDate / topupNearestExpiryAmount
   * (both optional/nullable — see OtterCoinBalance in types/coins.ts). This hook
   * passes them through unchanged as part of the whole coinBalance object; it does
   * not derive or format them (TS-014's indicator UI owns that).
   */
  coinBalance: OtterCoinBalance | undefined;
  /**
   * True when the family qualifies for the welcome-credit display:
   * - no active monthly subscription (monthlyCoins === 0)
   * - has a positive top-up balance (topupCoins > 0, holding the welcome coin)
   * - no in-progress session yet (inProgressSession == null)
   * This drives the welcome banner in CoinBalancePanels — purely a UI derivation,
   * the actual single-grant guarantee is enforced by TS-004 backend logic.
   */
  isNewFamily: boolean;
  /**
   * List of child accounts linked to this parent account.
   * US-008 Overview tab: displayed in the ChildProfilesGrid widget.
   * Empty array during loading or when no children are linked yet.
   */
  children: ChildSummary[];
  /**
   * The currently active in-progress tutoring session for any child in this family,
   * or null if no session is currently active.
   * US-008 Overview tab: displayed via InProgressSessionBanner widget.
   */
  inProgressSession: InProgressSession | null;
  /**
   * TS-007 (SR6, API-004): ALL active English sessions across the parent's
   * children (Math Coach excluded, <=1 per child) — the same array returned by
   * GET /parent/live-sessions, embedded here so the Overview needs no extra call.
   * Empty array during loading or when the backend omits the key (older cached
   * payloads before this backend change deploys).
   * US-007 Overview tab: drives the "View all live sessions" link (shown only
   * when liveSessions.length > 1).
   */
  liveSessions: LiveSession[];
  /**
   * Optional engagement metrics (pebbles earned, streak days, skill highlight).
   * US-014 Overview tab: displayed via RecentActivitySummary widget.
   * undefined when the backend does not include the engagementSummary key.
   * TO-LL-036: undefined means absent from backend response — not an error to mask.
   * TO-LL-044: fields within EngagementSummary use exact camelCase keys from the contract.
   */
  engagementSummary: EngagementSummary | undefined;
  /**
   * TS-009: the parent's current subscription-plan identity, resolved server-side.
   * - activePlanName: display name (e.g. "Practice"); null when no active plan / unresolved.
   * - activePlanKey: 'starter' | 'practice' | 'family' | null.
   * - subscriptionStatus: 'active' | 'cancelled' | null.
   * Consumed by the Plan & Coins tab's combined "Your Subscription" card.
   */
  activePlanKey: string | null;
  activePlanName: string | null;
  subscriptionStatus: string | null;
  /**
   * BUG-20260703-154123: The parent's authoritative display name for the greeting/chip.
   * Sourced from GET /parent/dashboard parentDisplayName (the parent's DynamoDB user record).
   * null while loading or when absent from the payload (graceful degradation to email fallback).
   * TO-LL-044: exact camelCase key from the API contract.
   */
  parentDisplayName: string | null;
  /**
   * Whether the parent has already seen the first-login welcome banner (persisted
   * server-side, cross-device). undefined while loading / when the payload predates
   * the field; the page treats undefined as "not yet known" and false as "show".
   * TO-LL-044: exact camelCase key from GET /parent/dashboard.
   */
  hasSeenWelcome: boolean | undefined;
  /**
   * Number of free session credits granted on sign-up (coin_config welcomeCoinAmount).
   * Drives the welcome banner copy so it always matches the actual grant. undefined
   * while loading / when the payload predates the field / when the backend's config
   * read degraded — the banner then falls back to the top-up balance.
   * TO-LL-044: exact camelCase key from GET /parent/dashboard.
   */
  welcomeCoinAmount: number | undefined;
  /**
   * first-run-guide (T7): the SERVER's current time, passed through unchanged.
   * Any "how long since X" decision must use this, never the device clock. undefined when
   * absent, in which case time-based retirement does not fire (the safe direction).
   */
  serverTime: string | undefined;
  /** Loading state — true during initial fetch */
  isLoading: boolean;
  /** Error object if fetch failed */
  error: Error | null;
  /** Function to manually refetch */
  refetch: () => void;
}

/**
 * Hook for fetching and managing parent dashboard and coin balance data.
 * Uses React Query with 30s staleTime (consistent with useDashboard convention).
 * Refetch-on-window-focus is sufficient for a balance display; no polling needed.
 */
export function useParentDashboard(): UseParentDashboardReturn {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<ParentDashboardResponse, Error>({
    queryKey: ['parent', 'dashboard'],
    queryFn: () => getParentDashboard(),
    staleTime: 30000, // 30 seconds — consistent with useDashboard
  });

  const coinBalance = data?.coinBalance;

  // Derive the welcome-credit display flag from response data only.
  // Conditions (all must be true):
  //   1. data is loaded (not undefined)
  //   2. No active monthly subscription bucket (monthlyCoins === 0)
  //   3. Positive top-up bucket (topupCoins > 0) — the welcome coin lives here
  //   4. No in-progress session yet (inProgressSession == null)
  const isNewFamily: boolean =
    !!data &&
    data.inProgressSession == null &&
    (coinBalance?.monthlyCoins ?? 1) === 0 &&
    (coinBalance?.topupCoins ?? 0) > 0;

  // US-008 (T4): expose children and inProgressSession from the same single query.
  // Defaults to [] / null during loading so callers never handle undefined.
  const children: ChildSummary[] = data?.children ?? [];
  const inProgressSession: InProgressSession | null = data?.inProgressSession ?? null;

  // TS-007 (SR6): expose the additively-embedded liveSessions[] (API-004) from the
  // same single query — no second fetch. Defaults to [] when absent (older cached
  // payloads before this backend change deploys, or genuinely zero live sessions).
  const liveSessions: LiveSession[] = data?.liveSessions ?? [];

  // US-014 (T1): expose engagementSummary from the same single query (no second fetch).
  // Passes data?.engagementSummary through unchanged — undefined when the key is absent.
  // TO-LL-036: we do NOT substitute a fallback default here; undefined = not yet available from
  //   backend, and the RecentActivitySummary component omits chips gracefully for undefined fields.
  // TO-LL-044: the EngagementSummary type uses exact camelCase keys matching the API contract.
  const engagementSummary: EngagementSummary | undefined = data?.engagementSummary;

  // TS-009: expose the resolved subscription-plan identity (display name + key + status)
  // for the Plan & Coins tab's combined "Your Subscription" card. Null-coalesced so callers
  // never handle undefined; null means no active plan / not yet resolved.
  const activePlanKey: string | null = data?.activePlanKey ?? null;
  const activePlanName: string | null = data?.activePlanName ?? null;
  const subscriptionStatus: string | null = data?.subscriptionStatus ?? null;

  // BUG-20260703-154123: expose the parent's authoritative display name.
  // Mirrors the activePlanName pass-through pattern exactly.
  // null while loading / absent — graceful degradation handled by ParentDashboardPage.
  const parentDisplayName: string | null = data?.parentDisplayName ?? null;

  // First-login welcome banner state — passed through unchanged. undefined while
  // loading / when the payload predates the field (page treats undefined as unknown).
  const hasSeenWelcome: boolean | undefined = data?.hasSeenWelcome;

  // Configured welcome-coin amount — passed through unchanged. undefined while
  // loading / when absent / when null (backend config read degraded); callers then
  // fall back to the top-up balance so the banner always shows a number.
  const welcomeCoinAmount: number | undefined = data?.welcomeCoinAmount ?? undefined;

  // first-run-guide (T7): server clock passthrough. Never substitute Date.now() here — the
  // whole point is that the comparison does not depend on the parent's device being correct.
  const serverTime: string | undefined = data?.serverTime ?? undefined;

  return {
    coinBalance,
    isNewFamily,
    children,
    inProgressSession,
    liveSessions,
    engagementSummary,
    activePlanKey,
    activePlanName,
    subscriptionStatus,
    parentDisplayName,
    hasSeenWelcome,
    welcomeCoinAmount,
    serverTime,
    isLoading,
    error,
    refetch: () => {
      refetch();
    },
  };
}
